import { useEffect } from 'react';
import { logger } from 'services';
import { taskCollection } from './task-collection';
import { apiTaskToStoreTask } from './to-store';
/**
 * Drives AgGrid row updates from the task collection. Fires only for
 * mutation-originated changes (same-tab optimistic edits, committed server
 * values, rollbacks, cross-tab sync) — NOT for fetch-sourced hydration (list
 * loads, drawer opens, WS refetches). Those don't change what the task looked
 * like from the grid's perspective, so refreshing for them caused unnecessary
 * forced repaints whenever a task drawer was opened.
 */ export const useTaskGridCollectionSync = (gridApi, fieldLookup)=>{
    useEffect(()=>{
        if (!gridApi) {
            return undefined;
        }
        const applyChanges = (changedIds)=>{
            if (!gridApi || gridApi.isDestroyed()) {
                return;
            }
            const refreshed = [];
            gridApi.forEachNode((node)=>{
                var _node_data;
                const id = (_node_data = node.data) === null || _node_data === void 0 ? void 0 : _node_data.taskId;
                if (id == null || !changedIds.has(id)) {
                    return;
                }
                const task = taskCollection.get(id);
                if (task) {
                    node.setData(apiTaskToStoreTask(task, fieldLookup));
                    refreshed.push(node);
                }
            });
            if (refreshed.length === 0) {
                return;
            }
            // `setData` alone updates the node but does not reliably repaint its
            // cells on the server-side row model: the rendered cell keeps whatever
            // the row was fetched with, so a task that moved phase in another tab
            // stayed in its old phase on screen while the collection underneath was
            // already correct. The socket path has always paired the two — this is
            // the same pairing, for the collection-driven path.
            //
            // Guarded because this runs inside the collection's notification
            // cascade: a throw here does not just skip the repaint, it aborts every
            // subscriber queued behind this one.
            try {
                var _gridApi_refreshCells;
                (_gridApi_refreshCells = gridApi.refreshCells) === null || _gridApi_refreshCells === void 0 ? void 0 : _gridApi_refreshCells.call(gridApi, {
                    rowNodes: refreshed,
                    force: true
                });
            } catch (error) {
                logger.error('task grid refreshCells failed', {
                    error
                });
            }
        };
        // subscribeToMutations: same-tab optimistic overlay (insert/update/delete).
        // Fires via the collection's deferred flush — safe to call applyChanges directly.
        const unsubMutations = taskCollection.subscribeToMutations(applyChanges);
        // subscribeToWrites: mutation commits, rollbacks, and cross-tab sync.
        // Fires INLINE (synchronously on the ingest hot path) — batch ids and defer
        // via queueMicrotask so we don't block the push caller.
        let pendingIds = new Set();
        let flushScheduled = false;
        const flush = ()=>{
            flushScheduled = false;
            const ids = pendingIds;
            pendingIds = new Set();
            applyChanges(ids);
        };
        const unsubWrites = taskCollection.subscribeToWrites((event)=>{
            // Skip fetch-sourced hydration: list loads, drawer opens, WS refetches.
            if (event.origin === 'push' && event.source === 'fetch') return;
            // Skip optimistic overlays — subscribeToMutations covers those above,
            // so we avoid a double refresh for every same-tab field edit.
            if (event.origin === 'optimistic') return;
            // Skip memory management — no meaningful data change for grid rows.
            if (event.origin === 'purge' || event.origin === 'clear') return;
            event.upserts.forEach((t)=>pendingIds.add(t.taskId));
            event.deletes.forEach((id)=>pendingIds.add(id));
            if (pendingIds.size > 0 && !flushScheduled) {
                flushScheduled = true;
                queueMicrotask(flush);
            }
        });
        return ()=>{
            unsubMutations();
            unsubWrites();
        };
    }, [
        gridApi,
        fieldLookup
    ]);
};
